import type { SampleType } from "@/contracts/base";
import { parseToDate } from "./date";

interface Sample {
  borehole: string;
  sample: string;
  depth_m: number;
  type: string;
  length_cm: number;
  diameter_mm: number;
  mass_g: number;
  location: string;
  main_soil: string;
  remarks: string;
  comments: string;
  tests: {
    offshore: string[];
    onshore: string[];
  };

  [key: string]: unknown;
}

export interface ImportedLtafs {
  properties: Record<string, string>;
  tests: Record<string, string>;
  totals: {
    offshore: Record<string, number>;
    onshore: Record<string, number>;
  };
  samples: Sample[];
}

enum Mode {
  Properties,
  Totals,
  Samples,
}

// function translateDate(serial: number) {
//   const offset = new Date(1899, 11, 30).getTime();
//   const time = 24 * 60 * 60 * 1000 * serial + offset;
//   return new Date(time).toISOString().split('T')[0];
// }

function rowHasValue(row: Record<string, unknown>, search: string): boolean {
  return Object.values(row).some((x) => typeof x === "string" && x.toLowerCase().includes(search));
}

function cellText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

const knownColumnsMapping: Record<string, string> = {
  "borehole number": "borehole",
  sample: "sample",
  "depth from (m)": "depth_m",
  "sample type": "type",
  "sample length (cm)": "length_cm",
  "sample diameter (mm)": "diameter_mm",
  "mass (g)": "mass_g",
  "sample location": "location",
  "main soil": "main_soil",
  remarks: "remarks",
  comments: "comments",
};

export function transformLtafSheet(source: Record<string, unknown>[]): ImportedLtafs {
  const result: ImportedLtafs = {
    properties: {},
    tests: {},
    totals: {
      offshore: {},
      onshore: {},
    },
    samples: [],
  };

  const headerMapping: Record<string, string> = {};
  const reverseHeaderMapping: Record<string, string> = {};

  let mode = Mode.Properties;
  source.forEach((row) => {
    switch (mode) {
      case Mode.Properties:
      //  break;
      case Mode.Totals:
        if (rowHasValue(row, "borehole number")) {
          for (const [k, v] of Object.entries(row)) {
            if (typeof v !== "string") continue;
            const vl = v.trim().toLowerCase();
            if (!vl) continue;
            if (vl in knownColumnsMapping) {
              headerMapping[k] = knownColumnsMapping[vl];
              reverseHeaderMapping[headerMapping[k]] = k;
            } else {
              result.tests[k] = v.trim();
            }
          }
          mode = Mode.Samples;
        }
        break;
      case Mode.Samples: {
        const borehole_idx = reverseHeaderMapping.borehole;
        const boreholeValue = row[borehole_idx];
        if (!(borehole_idx in row) || typeof boreholeValue !== "string" || boreholeValue.trim().length === 0) break;
        const sample: { tests: { onshore: string[]; offshore: string[] }; [key: string]: unknown } = {
          tests: { onshore: [], offshore: [] },
        };
        // first known columns
        Object.entries(headerMapping).forEach(([k, p]) => {
          sample[p] = cellText(row[k]);
        });
        Object.keys(result.tests).forEach((k) => {
          const v = cellText(row[k]);
          if (v === "#") sample.tests.offshore.push(k);
          else if (v === "/") sample.tests.onshore.push(k);
          else if (v.length > 0) {
            sample[result.tests[k]] = v;
            // TODO - figure out if need to set it as a property for all of the samples
          }
        });
        result.samples.push(sample as Sample);
        break;
      }
    }
  });

  return result;
}

export interface SampleQrRecord {
  sampleId?: string;
  project?: string;
  location?: string;
  sampleReference?: string;
  topDepthM?: number;
  baseDepthM?: number;
  type?: string;
  collectionId?: string;
  testCode?: string;
}

export function parseSampleQrString(qrString: string): SampleQrRecord {
  const parts = qrString.split("|");
  const result: SampleQrRecord = {};

  if (parts.length < 7) return result;

  const getValue = (index: number): string | undefined => {
    if (index >= parts.length) return undefined;
    const value = parts[index].trim();
    return value.length > 0 ? value : undefined;
  };

  const getNumericValue = (index: number): number | undefined => {
    const value = getValue(index);
    if (!value) return undefined;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const sampleId = getValue(0);
  if (sampleId && /^F-[A-Z0-9]{6}-[A-Z0-9]{4}$/.test(sampleId)) {
    result.sampleId = sampleId;
  }

  result.project = getValue(1);
  result.location = getValue(2);
  result.sampleReference = getValue(3);
  result.topDepthM = getNumericValue(4);
  result.baseDepthM = getNumericValue(5);
  result.type = getValue(6);

  if (parts.length >= 8) {
    result.collectionId = getValue(7);
  }

  if (parts.length >= 9) {
    result.testCode = getValue(8);
  }

  return result;
}

export function withHairSpace(text: string): string {
  const hairSpace = "\u200A";
  return text.split("").join(hairSpace);
}

export function mapSampleType(source?: string | null): SampleType | null {
  const s = (source ?? "").trim().toUpperCase();

  if (s === "" || s === "UNKNOWN" || s === "UNDEFINED") return null;

  if (s === "B" || s === "BULK" || s === "LB" || s === "BAG") return "bag";
  if (s === "WAX") return "wax";
  if (s === "L" || s === "LINER") return "liner";
  if (s === "T" || s === "TUBE") return "tube";
  if (s === "JAR") return "jar";
  if (s === "QUART") return "quart";
  if (s === "BUCKET") return "bucket";
  if (s === "BATCH") return "batch";
  if (s === "BLOCK" || s === "BOX") return "block";

  if (
    s === "CORE" ||
    s === "C" ||
    s === "CS" ||
    s === "CL" ||
    s === "GC" ||
    s === "VC" ||
    s === "BC" ||
    s === "ST38" ||
    s === "ST67" ||
    s === "ST100" ||
    s === "PS" ||
    s === "L100" ||
    s === "L150" ||
    s === "GP" ||
    s === "SNC" ||
    s === "SH"
  ) {
    return "core";
  }

  return null;
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  if (typeof a === "object" && typeof b === "object") {
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => key in bObj && deepEqual(aObj[key], bObj[key]));
  }

  return false;
}

export function formatDateTime(isoDateString?: string | null): string {
  if (!isoDateString) return "";
  const d = parseToDate(isoDateString);
  return d ? d.toLocaleString(navigator.language || undefined) : isoDateString;
}

export function formatDate(isoDateString?: string | null): string {
  if (!isoDateString) return "";
  const d = parseToDate(isoDateString);
  return d ? d.toLocaleDateString(navigator.language || undefined) : isoDateString;
}

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const downloadFile = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  URL.revokeObjectURL(url);
};

export const capitalize = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export * from "./caretToEnd";
export * from "./depthInput";
export * from "./extractReportErrorMessage";
export * from "./number-extensions";

export function toDistinctOptions(options: string[], currentValue: string | null): string[] {
  if (!currentValue || options.includes(currentValue)) return options;
  return [...options, currentValue];
}

// Object analogue of toDistinctOptions: appends the selected item (matched by id) when it is
// no longer in the source list, so a dropdown still shows a saved-but-since-removed selection.
export function includeSelectedOption<T extends { id: string }>(options: T[], selected: T | null | undefined): T[] {
  if (!selected || options.some((option) => option.id === selected.id)) return options;
  return [...options, selected];
}

export function equipmentIds<T extends { id: string }>(options: readonly (T | string)[] | undefined): string[] {
  return (options ?? []).map((option) => (typeof option === "string" ? option : option.id));
}

export function findEquipment<T extends { id: string }>(
  options: readonly (T | string)[] | undefined,
  id: string | null | undefined,
): T | undefined {
  if (!id) return undefined;
  return options?.find((option): option is T => typeof option !== "string" && option.id === id);
}
